import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Team } from '@lms/shared-types'
import { Card } from '../components/primitives/Card'
import { EditedBadge } from '../components/primitives/EditedBadge'
import { FadeIn } from '../components/primitives/FadeIn'
import { PageHead } from '../components/primitives/PageHead'
import { Modal } from '../components/primitives/Modal'
import { Severity } from '../components/primitives/Severity'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import {
  createPeakPeriod,
  listPeakPeriods,
  updatePeakPeriod,
  type ObservanceKind,
  type PeakPeriod,
  type PeakPeriodActiveFilter,
  type PeakPeriodCreate,
} from '../api/peak-periods'
import { listTeams } from '../api/teams'
import { fmtDateRange } from '../lib/format'
import { flags } from '../lib/feature-flags'
import { NotFoundPage } from './NotFoundPage'

const KIND_LABEL: Record<ObservanceKind, string> = {
  religious: 'Religious',
  cultural: 'Cultural',
}

const KIND_SEVERITY: Record<ObservanceKind, 'info' | 'medium'> = {
  religious: 'medium',
  cultural: 'info',
}

/** HR-managed catalogue of peak periods (RFC 005, policy §3).
 *
 *  Each row links a date window to an observance the 5-factor 104-pt
 *  scorer awards the 35-pt "Religious Significance" factor against.
 *  HR owns the catalogue end-to-end — managers and employees never see
 *  this surface.
 */
export function HrPeakPeriodsPage(): JSX.Element {
  // Flag-gated per the LMS feature-flag standard. When OFF, the route
  // surfaces a 404 so the path does not leak the feature's existence
  // before UAT. ``flags.peakPeriod104ptScoring`` is a compile-time
  // import-meta constant, so the conditional doesn't violate the
  // rules-of-hooks contract — it always evaluates to the same branch
  // for a given build.
  if (!flags.peakPeriod104ptScoring) {
    return <NotFoundPage />
  }
  return <HrPeakPeriodsPageInner />
}

function HrPeakPeriodsPageInner(): JSX.Element {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PeakPeriod | null>(null)
  const [filter, setFilter] = useState<PeakPeriodActiveFilter>('true')

  const peakPeriodsQuery = useQuery({
    queryKey: ['peak-periods', filter],
    queryFn: () => listPeakPeriods(filter),
  })
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams })

  const teamsById = new Map<string, string>()
  for (const t of teamsQuery.data ?? []) teamsById.set(t.id, t.name)

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['peak-periods'] })
  }

  // Deactivation is a soft-delete: PATCH `active=false` keeps the row in
  // the audit trail and lets HR re-enable it later via the Inactive
  // filter. Mirrors the Blackouts row pattern (one-click + confirm dialog).
  const deactivateMutation = useMutation({
    mutationFn: (id: string) => updatePeakPeriod(id, { active: false }),
    onSuccess: invalidate,
  })

  return (
    <HrAccessGuard>
      <PageHead
        eyebrow="HR"
        title="Peak periods"
        subtitle="Date windows where the 104-pt priority scorer awards the religious-significance factor (RFC 005, policy §3)."
        actions={
          <button
            type="button"
            data-testid="new-peak-period"
            onClick={() => setCreating(true)}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--steel-blue)',
              color: 'var(--surface)',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            + New peak period
          </button>
        }
      />

      <FilterBar value={filter} onChange={setFilter} />

      <Card padding={0} style={{ overflow: 'hidden' }}>
        {peakPeriodsQuery.isLoading ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <ThinkingDots label="Loading peak periods" />
          </div>
        ) : peakPeriodsQuery.isError ? (
          <div role="alert" style={{ padding: 40, textAlign: 'center', color: 'var(--danger-fg)' }}>
            Couldn’t load peak periods. Check your connection and try again.
          </div>
        ) : (peakPeriodsQuery.data ?? []).length === 0 ? (
          <FadeIn>
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: 'var(--text-3)',
              }}
            >
              No peak periods declared yet.
            </div>
          </FadeIn>
        ) : (
          <ul role="list" data-testid="peak-periods-list">
            {(peakPeriodsQuery.data ?? []).map((p, i) => (
              <FadeIn
                key={p.id}
                rise={4}
                delayMs={Math.min(i, 6) * 30}
                duration="slow"
              >
                <PeakPeriodRow
                  peakPeriod={p}
                  teamsById={teamsById}
                  isEditing={editing?.id === p.id}
                  onEdit={() => setEditing(p)}
                  onDeactivate={() => {
                    if (
                      window.confirm(
                        `Deactivate ${p.name}? Inactive peak periods aren't used by the priority scorer but remain in the audit trail.`,
                      )
                    ) {
                      deactivateMutation.mutate(p.id)
                    }
                  }}
                />
              </FadeIn>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New peak period"
      >
        {creating ? (
          <PeakPeriodForm
            mode="create"
            teams={teamsQuery.data ?? []}
            onClose={() => setCreating(false)}
            onSaved={() => {
              setCreating(false)
              invalidate()
            }}
          />
        ) : null}
      </Modal>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit peak period"
      >
        {editing ? (
          <PeakPeriodForm
            mode="edit"
            existing={editing}
            teams={teamsQuery.data ?? []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null)
              invalidate()
            }}
          />
        ) : null}
      </Modal>

    </HrAccessGuard>
  )
}

// ── Filter bar ────────────────────────────────────────────────────────────────

interface FilterBarProps {
  value: PeakPeriodActiveFilter
  onChange: (next: PeakPeriodActiveFilter) => void
}

const FILTER_OPTIONS: ReadonlyArray<{
  key: PeakPeriodActiveFilter
  label: string
}> = [
  { key: 'true', label: 'Active' },
  { key: 'false', label: 'Inactive' },
  { key: 'all', label: 'All' },
]

function FilterBar({ value, onChange }: FilterBarProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        marginBottom: 12,
        alignItems: 'center',
      }}
      role="tablist"
      aria-label="Peak period filter"
    >
      {FILTER_OPTIONS.map((opt) => {
        const selected = value === opt.key
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`filter-${opt.key}`}
            onClick={() => onChange(opt.key)}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: `1px solid ${selected ? 'var(--steel-blue)' : 'var(--border)'}`,
              background: selected ? 'var(--primary-bg)' : 'var(--surface)',
              color: selected ? 'var(--steel-blue)' : 'var(--text-2)',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Row ───────────────────────────────────────────────────────────────────────

interface PeakPeriodRowProps {
  peakPeriod: PeakPeriod
  teamsById: Map<string, string>
  onEdit: () => void
  onDeactivate: () => void
  isEditing?: boolean
}

function PeakPeriodRow({
  peakPeriod,
  teamsById,
  onEdit,
  onDeactivate,
  isEditing = false,
}: PeakPeriodRowProps): JSX.Element {
  const teamNames =
    peakPeriod.teamIds.length === 0
      ? 'All teams'
      : peakPeriod.teamIds.map((id) => teamsById.get(id) ?? '—').join(', ')

  return (
    <li
      data-testid="peak-period-row"
      data-peak-period-id={peakPeriod.id}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 18px',
        gap: 12,
        borderBottom: '1px solid var(--french-gray-2)',
        cursor: 'pointer',
        opacity: peakPeriod.active ? 1 : 0.6,
      }}
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit()
        }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {peakPeriod.name}
          </span>
          <Severity kind={KIND_SEVERITY[peakPeriod.observanceKind]}>
            {KIND_LABEL[peakPeriod.observanceKind]} · {peakPeriod.observance}
          </Severity>
          {!peakPeriod.active ? (
            <Severity kind="info">Inactive</Severity>
          ) : null}
          {isEditing ? <EditedBadge /> : null}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          {fmtDateRange(peakPeriod.startDate, peakPeriod.endDate)} · {teamNames}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          data-testid="edit-peak-period"
          onClick={(e) => {
            e.stopPropagation()
            onEdit()
          }}
          style={{
            padding: '6px 12px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Edit
        </button>
        {peakPeriod.active ? (
          <button
            type="button"
            data-testid="deactivate-peak-period"
            onClick={(e) => {
              e.stopPropagation()
              onDeactivate()
            }}
            style={{
              padding: '6px 12px',
              background: 'var(--surface)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Deactivate
          </button>
        ) : null}
      </div>
    </li>
  )
}

// ── Form ──────────────────────────────────────────────────────────────────────

type PeakPeriodFormProps =
  | {
      mode: 'create'
      teams: Team[]
      onClose: () => void
      onSaved: () => void
      existing?: never
    }
  | {
      mode: 'edit'
      existing: PeakPeriod
      teams: Team[]
      onClose: () => void
      onSaved: () => void
    }

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function PeakPeriodForm(props: PeakPeriodFormProps): JSX.Element {
  const { mode, teams, onClose, onSaved } = props

  const [form, setForm] = useState<PeakPeriodCreate>(() =>
    mode === 'edit'
      ? {
          name: props.existing.name,
          observance: props.existing.observance,
          observanceKind: props.existing.observanceKind,
          startDate: props.existing.startDate,
          endDate: props.existing.endDate,
          teamIds: [...props.existing.teamIds],
          active: props.existing.active,
        }
      : {
          name: '',
          observance: '',
          observanceKind: 'religious',
          startDate: todayIso(),
          endDate: todayIso(),
          teamIds: [],
          active: true,
        },
  )
  const [error, setError] = useState<string | null>(null)

  // Keep form in sync if the parent re-renders the modal with a different
  // row. Only relevant in edit mode — create mode keeps the user's draft.
  const existingId = mode === 'edit' ? props.existing.id : null
  useEffect(() => {
    if (props.mode === 'edit') {
      setForm({
        name: props.existing.name,
        observance: props.existing.observance,
        observanceKind: props.existing.observanceKind,
        startDate: props.existing.startDate,
        endDate: props.existing.endDate,
        teamIds: [...props.existing.teamIds],
        active: props.existing.active,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingId])

  const mutation = useMutation({
    mutationFn: async (input: PeakPeriodCreate): Promise<PeakPeriod> =>
      mode === 'edit'
        ? updatePeakPeriod(props.existing.id, input)
        : createPeakPeriod(input),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Save failed'
      setError(`${msg} (${status})`)
    },
  })

  const toggleTeam = (id: string): void => {
    setForm((f) => ({
      ...f,
      teamIds: f.teamIds.includes(id)
        ? f.teamIds.filter((x) => x !== id)
        : [...f.teamIds, id],
    }))
  }

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault()
    setError(null)
    if (form.name.trim().length === 0) {
      setError('Name is required.')
      return
    }
    if (form.observance.trim().length === 0) {
      setError('Observance is required (e.g. "Islam", "Chinese").')
      return
    }
    if (form.endDate < form.startDate) {
      setError('End date must be on or after start date.')
      return
    }
    mutation.mutate(form)
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-2)',
    marginBottom: 4,
    display: 'block',
  }
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--surface)',
    color: 'var(--text)',
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div>
        <label htmlFor="pp-name" style={labelStyle}>
          Name
        </label>
        <input
          id="pp-name"
          data-testid="pp-name"
          value={form.name}
          onChange={(e) =>
            setForm((f) => ({ ...f, name: e.target.value }))
          }
          style={inputStyle}
          placeholder="Hari Raya 2027"
          maxLength={200}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label htmlFor="pp-observance" style={labelStyle}>
            Observance
          </label>
          <input
            id="pp-observance"
            data-testid="pp-observance"
            value={form.observance}
            onChange={(e) =>
              setForm((f) => ({ ...f, observance: e.target.value }))
            }
            style={inputStyle}
            placeholder="Islam"
            maxLength={100}
          />
        </div>
        <div>
          <label htmlFor="pp-observance-kind" style={labelStyle}>
            Kind
          </label>
          <select
            id="pp-observance-kind"
            data-testid="pp-observance-kind"
            value={form.observanceKind}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                observanceKind: e.target.value as ObservanceKind,
              }))
            }
            style={inputStyle}
          >
            <option value="religious">Religious (matches religion)</option>
            <option value="cultural">Cultural (matches race)</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label htmlFor="pp-start" style={labelStyle}>
            Start date
          </label>
          <input
            id="pp-start"
            type="date"
            data-testid="pp-start"
            value={form.startDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, startDate: e.target.value }))
            }
            style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="pp-end" style={labelStyle}>
            End date
          </label>
          <input
            id="pp-end"
            type="date"
            data-testid="pp-end"
            value={form.endDate}
            onChange={(e) =>
              setForm((f) => ({ ...f, endDate: e.target.value }))
            }
            style={inputStyle}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Team scope</label>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: '8px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--surface)',
            maxHeight: 160,
            overflowY: 'auto',
          }}
        >
          {teams.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
              Loading teams…
            </span>
          ) : (
            teams.map((t) => {
              const selected = form.teamIds.includes(t.id)
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`pp-team-${t.id}`}
                  onClick={() => toggleTeam(t.id)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: `1px solid ${selected ? 'var(--steel-blue)' : 'var(--border)'}`,
                    background: selected ? 'var(--primary-bg)' : 'var(--surface)',
                    color: selected ? 'var(--steel-blue)' : 'var(--text-2)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t.name}
                </button>
              )
            })
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
          Empty = applies to all teams.
        </div>
      </div>

      <label
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          fontSize: 13,
          color: 'var(--text-2)',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          data-testid="pp-active"
          checked={form.active}
          onChange={(e) =>
            setForm((f) => ({ ...f, active: e.target.checked }))
          }
        />
        Active
      </label>

      {error ? (
        <div
          role="alert"
          style={{
            padding: '8px 10px',
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          style={{
            padding: '8px 14px',
            background: 'var(--surface)',
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 600,
            cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="pp-submit"
          disabled={mutation.isPending}
          style={{
            padding: '8px 14px',
            background: 'var(--steel-blue)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: mutation.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {mutation.isPending
            ? 'Saving…'
            : mode === 'edit'
              ? 'Save changes'
              : 'Create'}
        </button>
      </div>
    </form>
  )
}
